const DB_NAME = "cg-wizard-photos";
const STORE_NAME = "photos";
const STORE_CONTENT_JOB_SOURCES = "content-job-sources";
const DB_VERSION = 2;

function photoKey(sessionId: string, imageId: string): string {
  return `${sessionId}::${imageId}`;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("indexedDB is not available"));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error ?? new Error("failed to open IndexedDB"));
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
      if (!db.objectStoreNames.contains(STORE_CONTENT_JOB_SOURCES)) {
        // keyPath jobId — id хранится в самом объекте.
        db.createObjectStore(STORE_CONTENT_JOB_SOURCES, { keyPath: "jobId" });
      }
    };
  });
}

export async function putWizardPhoto(sessionId: string, imageId: string, blob: Blob): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error ?? new Error("failed to write wizard photo"));
    };
    tx.objectStore(STORE_NAME).put(blob, photoKey(sessionId, imageId));
  });
}

export async function getWizardPhoto(sessionId: string, imageId: string): Promise<Blob | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const request = tx.objectStore(STORE_NAME).get(photoKey(sessionId, imageId));
    request.onsuccess = () => {
      db.close();
      const value = request.result;
      resolve(value instanceof Blob ? value : null);
    };
    request.onerror = () => {
      db.close();
      reject(request.error ?? new Error("failed to read wizard photo"));
    };
  });
}

export async function deleteWizardPhoto(sessionId: string, imageId: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error ?? new Error("failed to delete wizard photo"));
    };
    tx.objectStore(STORE_NAME).delete(photoKey(sessionId, imageId));
  });
}

/**
 * Метаданные source-картинок job-а, сохранённые для restore после reload.
 * Не хранит сами файлы — только id, slotIndex, sourceUrl (уже на бэке) и
 * выбранный variantId. Используется для мгновенного восстановления UI
 * шага «Улучшение» до прихода snapshot/SSE.
 */
export type ContentJobSourcesEntry = {
  jobId: string;
  ownerId: string;
  slots: Array<{
    slotIndex: number;
    imageId: string;
    sourceUrl: string;
    selectedVariantId?: string;
  }>;
  savedAt: number;
};

export async function putContentJobSources(entry: ContentJobSourcesEntry): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_CONTENT_JOB_SOURCES, "readwrite");
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error ?? new Error("failed to write content job sources"));
    };
    tx.objectStore(STORE_CONTENT_JOB_SOURCES).put(entry);
  });
}

export async function getContentJobSources(jobId: string): Promise<ContentJobSourcesEntry | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CONTENT_JOB_SOURCES, "readonly");
    const request = tx.objectStore(STORE_CONTENT_JOB_SOURCES).get(jobId);
    request.onsuccess = () => {
      db.close();
      const value = request.result;
      resolve(value && typeof value === "object" ? (value as ContentJobSourcesEntry) : null);
    };
    request.onerror = () => {
      db.close();
      reject(request.error ?? new Error("failed to read content job sources"));
    };
  });
}

export async function deleteContentJobSources(jobId: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_CONTENT_JOB_SOURCES, "readwrite");
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error ?? new Error("failed to delete content job sources"));
    };
    tx.objectStore(STORE_CONTENT_JOB_SOURCES).delete(jobId);
  });
}

/** Remove all locally stored photos for a wizard session. */
export async function clearWizardPhotoSession(sessionId: string): Promise<void> {
  const db = await openDb();
  const prefix = `${sessionId}::`;
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const request = store.openCursor();
    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor) {
        if (typeof cursor.key === "string" && cursor.key.startsWith(prefix)) {
          cursor.delete();
        }
        cursor.continue();
      }
    };
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error ?? new Error("failed to clear wizard photos"));
    };
  });
}
